/**
 * API e stato server della consultazione portafogli.
 *
 * ⚠️ I campi monetari sono STRINGHE decimali (ADR-0006): si formattano con
 * `lib/format.ts` e non si convertono in `number` per fare calcoli.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssetDomain } from '@/lib/asset-domain';
import { api } from '@/lib/api-client';

export type PortfolioSummary = {
  id: string;
  name: string;
  portfolioType: string;
  /** Gli ambiti che il portafoglio può ospitare (SPEC-026 §2.2): `paper` e `mixed` entrambi. */
  domains: AssetDomain[];
  baseCurrency: string;
  isActive: boolean;
  createdAt: string;
  platform: { id: string; code: string; name: string };
  latestSnapshot: {
    id: string;
    snapshotDate: string;
    totalValue: string | null;
    currency: string;
    holdingsCount: number;
  } | null;
  transactionCount: number;
  /** Liquidità: somma dei movimenti. Esposta per ogni tipo di portafoglio. */
  cashBalance: string | null;
  /** Titoli + liquidità: la base delle percentuali di wishlist e dei limiti. */
  totalWithCash: string | null;
};

/** Storia dei movimenti di uno strumento: ciò che lo snapshot da solo non dice. */
export type HoldingHistory = {
  firstBuyDate: string | null;
  lastTransactionDate: string | null;
  investedEur: string;
  realizedPlEur: string;
  feesPaidEur: string;
  taxesPaidEur: string;
  dividendsReceivedEur: string;
  transactionsCount: number;
  netQuantity: string;
};

export type Holding = {
  id: string;
  quantity: string;
  avgBuyPrice: string | null;
  currentPrice: string | null;
  marketValue: string | null;
  unrealizedPl: string | null;
  unrealizedPlPct: string | null;
  weightPct: string | null;
  currency: string;
  instrument: {
    id: string;
    name: string;
    symbol: string | null;
    isin: string | null;
    instrumentType: string;
    /** Il ticker del broker, con la posizione: è ciò che dice all'ordine manuale se la mappatura c'è. */
    trading212Ticker: string | null;
  };
  history: HoldingHistory | null;
};

export type SnapshotPoint = {
  snapshotDate: string;
  /** Solo le posizioni: scende quando si vende, anche in guadagno. */
  totalValue: string | null;
  currency: string;
  holdingsCount: number;
  /** Liquidita' a quella data: somma dei movimenti fino a fine giornata. */
  cashBalance: string;
  /** Titoli + liquidita': e' qui che si legge la perdita effettiva (F-116). */
  totalWithCash: string | null;
  /** Versamenti meno prelievi arrivati dopo il punto precedente (F-183). Zero sul primo. */
  netFlow: string;
  /** Titoli + liquidita' meno i versamenti netti dal primo punto valorizzato (F-183). */
  totalNetOfFlows: string | null;
};

export type PortfolioHistory = {
  points: SnapshotPoint[];
  currency: string;
  /** Versamenti meno prelievi nella finestra, dopo il punto di partenza. */
  netFlowsInWindow: string;
  /** Rendimento della finestra al netto dei versamenti; `null` senza punti valorizzati. */
  gainInWindow: string | null;
};

/** Un movimento di un titolo nel giorno: spiega perché la curva del rendimento si è mossa (F-182). */
export type PerformanceOperation = {
  id: string;
  txType: string;
  txDate: string;
  instrument: { id: string; name: string; symbol: string | null } | null;
  quantity: string | null;
  price: string | null;
  amount: string;
  currency: string;
  /** Solo sulle vendite: quanto è passato dal non realizzato al realizzato. */
  sale: { averageCost: string; realized: string; costUnknown: boolean } | null;
};

/** Lo stato del portafoglio a fine giornata, cumulato: non il guadagno del giorno. */
export type PerformanceDay = {
  date: string;
  /** `null` = nessuno snapshot quel giorno: il valore di mercato non è noto. */
  unrealized: string | null;
  realized: string;
  combined: string | null;
  /** Posizioni escluse dalla non realizzata perché senza valore o senza costo. */
  unvaluedHoldings: number;
  operations: PerformanceOperation[];
};

export type PortfolioPerformance = {
  currency: string;
  from: string | null;
  to: string;
  days: PerformanceDay[];
  /** L'ultimo valore di ciascuna curva prima della finestra: l'apertura della prima candela. */
  opening: { unrealized: string | null; realized: string; combined: string | null };
};

export type PortfolioTransaction = {
  id: string;
  txType: string;
  txDate: string;
  quantity: string | null;
  price: string | null;
  amount: string;
  fees: string;
  taxes: string;
  currency: string;
  /**
   * Solo sulle vendite (F-118): quanto ha reso **questa** vendita, con il costo
   * medio di carico al momento in cui e' avvenuta.
   */
  sale: {
    /** Costo medio per unita' prima della vendita: il termine di paragone. */
    averageCost: string;
    costRemoved: string;
    proceeds: string;
    /** Negativo su una perdita. */
    realized: string;
    /** Costo ignoto: il realizzato coincide con l'incasso ed e' sovrastimato. */
    costUnknown: boolean;
  } | null;
  notes: string | null;
  instrument: { id: string; name: string; symbol: string | null; isin: string | null } | null;
  // SPEC-008: provenienza, dettagli d'ordine e aggancio opzionale a un'analisi AI.
  source: string;
  orderType: string | null;
  limitPrice: string | null;
  stopPrice: string | null;
  takeProfitPrice: string | null;
  timeInForce: string | null;
  matchedAt: string | null;
  sourceAnalysisId: string | null;
  /**
   * Valore di carico di un trasferimento in entrata (F-174): `null` = entra a
   * costo zero. `market` = prezzo dell'asset alla ricezione, calcolato dal
   * sistema; `manual` = scritto dall'utente.
   */
  costBasis: string | null;
  costBasisSource: 'market' | 'manual' | null;
  /**
   * La proposta di auto-trading che ha prodotto il movimento (F-096). `null`
   * per un movimento importato o registrato a mano: e' cio' che distingue
   * «l'ho deciso io» da «lo ha deciso il motore».
   */
  proposalId: string | null;
};

/** Corpo di `POST /portfolios/:id/transactions` (SPEC-008 §5). */
export type CreateTransactionInput = {
  instrumentId?: string;
  isin?: string;
  symbol?: string;
  txType: string;
  txDate: string;
  quantity?: number;
  price?: number;
  amount: number;
  fees?: number;
  taxes?: number;
  currency: string;
  notes?: string;
  orderType?: string;
  limitPrice?: number;
  stopPrice?: number;
  takeProfitPrice?: number;
  timeInForce?: string;
  sourceAnalysisId?: string;
};

export type InstrumentPositionPoint = {
  snapshotDate: string;
  quantity: string;
  marketValue: string | null;
  currentPrice: string | null;
  currency: string;
};

export type PortfolioInstrumentDetail = {
  portfolio: { id: string; name: string; baseCurrency: string };
  instrument: {
    id: string;
    name: string;
    symbol: string | null;
    isin: string | null;
    instrumentType: string;
    /** Il ticker del broker, con la posizione (F-139). */
    trading212Ticker: string | null;
    currency: string;
  };
  /** `null` per una posizione chiusa: resta comunque lo storico dei movimenti. */
  currentPosition: {
    quantity: string;
    currentPrice: string | null;
    marketValue: string | null;
    weightPct: string | null;
    unrealizedPl: string | null;
    unrealizedPlPct: string | null;
    currency: string;
    snapshotDate: string;
  } | null;
  history: HoldingHistory | null;
  positionHistory: InstrumentPositionPoint[];
};

/** Risultato di ricerca nel catalogo strumenti già esistente. */
export type InstrumentSearchResult = {
  id: string;
  name: string;
  isin: string | null;
  symbol: string | null;
  instrumentType: string;
  currency: string;
};

/** Candidato OpenFIGI per un titolo non ancora a catalogo (ISIN/ticker/nome libero). */
export type InstrumentCandidate = {
  ticker: string;
  exchange: string | null;
  figi: string | null;
  currency: string | null;
  securityType: string | null;
  name: string | null;
};

export type CreateInstrumentInput = {
  isin?: string;
  ticker: string;
  exchange?: string | null;
  figi?: string | null;
  currency: string;
  securityType?: string | null;
  name?: string;
  /** Ticker del broker, quando il candidato viene dal catalogo Trading 212. */
  trading212Ticker?: string;
};

/** Ricerca nel catalogo esistente (nome, ISIN o simbolo). */
export function useSearchInstruments() {
  return useMutation({
    mutationFn: (query: string) =>
      api.get<{ items: InstrumentSearchResult[] }>(`/instruments?q=${encodeURIComponent(query)}`),
  });
}

/** Candidati OpenFIGI per un titolo non ancora a catalogo: ISIN → mapping, testo libero → ricerca. */
export function useResolveInstrumentCandidates() {
  return useMutation({
    mutationFn: (query: string) =>
      api.post<{ items: InstrumentCandidate[] }>('/instruments/resolve-candidates', { query }),
  });
}

/** Crea uno strumento già risolto a partire da un candidato scelto nel dialog. */
export function useCreateInstrument() {
  return useMutation({
    mutationFn: (input: CreateInstrumentInput) => api.post<InstrumentSearchResult>('/instruments', input),
  });
}

export const portfolioKeys = {
  list: ['portfolios', 'list'] as const,
  detail: (id: string) => ['portfolios', 'detail', id] as const,
  holdings: (id: string) => ['portfolios', 'holdings', id] as const,
  history: (id: string, days: number | null = null) => ['portfolios', 'history', id, days] as const,
  performance: (id: string, days: number | null) => ['portfolios', 'performance', id, days] as const,
  transactions: (id: string) => ['portfolios', 'transactions', id] as const,
  instrument: (id: string, instrumentId: string) =>
    ['portfolios', 'instrument', id, instrumentId] as const,
};

export function usePortfolios() {
  return useQuery({
    queryKey: portfolioKeys.list,
    queryFn: ({ signal }) =>
      api.get<{ items: PortfolioSummary[]; total: number }>('/portfolios', signal),
  });
}

export type Platform = { id: string; code: string; name: string; type: string; logoUrl: string | null };

/** Lookup di sola lettura (SPEC §6.2): serve al form di creazione portafoglio. */
export function usePlatforms() {
  return useQuery({
    queryKey: ['platforms', 'list'] as const,
    queryFn: ({ signal }) => api.get<{ items: Platform[] }>('/platforms', signal),
  });
}

export type CreatePortfolioBody = {
  name: string;
  portfolioType: string;
  platformId: string;
  baseCurrency?: string;
};

/** Creazione manuale di un portafoglio (TODO.md): finora si creavano solo dall'import. */
export function useCreatePortfolio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePortfolioBody) => api.post<PortfolioSummary>('/portfolios', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.list });
    },
  });
}

export function usePortfolio(id: string) {
  return useQuery({
    queryKey: portfolioKeys.detail(id),
    queryFn: ({ signal }) => api.get<PortfolioSummary>(`/portfolios/${id}`, signal),
  });
}

export function usePortfolioHoldings(id: string) {
  return useQuery({
    queryKey: portfolioKeys.holdings(id),
    queryFn: ({ signal }) =>
      api.get<{ snapshotDate: string | null; items: Holding[] }>(
        `/portfolios/${id}/holdings`,
        signal,
      ),
  });
}

/** Andamento del valore. `days` null = tutto lo storico. */
export function usePortfolioHistory(id: string, days: number | null = null) {
  return useQuery({
    queryKey: portfolioKeys.history(id, days),
    queryFn: ({ signal }) =>
      api.get<PortfolioHistory>(`/portfolios/${id}/history${days ? `?days=${days}` : ''}`, signal),
    // Cambiando periodo il grafico resta al suo posto finché arrivano i dati nuovi.
    placeholderData: keepPreviousData,
  });
}

/** Rendimento giorno per giorno (F-182). `days` null = tutto lo storico. */
export function usePortfolioPerformance(id: string, days: number | null) {
  return useQuery({
    queryKey: portfolioKeys.performance(id, days),
    queryFn: ({ signal }) =>
      api.get<PortfolioPerformance>(`/portfolios/${id}/performance${days ? `?days=${days}` : ''}`, signal),
    // Cambiando periodo il grafico resta al suo posto finché arrivano i dati nuovi.
    placeholderData: keepPreviousData,
  });
}

export function usePortfolioTransactions(
  id: string,
  options: { limit?: number; instrumentId?: string; enabled?: boolean } = {},
) {
  const limit = options.limit ?? 25;
  const instrumentId = options.instrumentId;

  const query = new URLSearchParams({ limit: String(limit) });
  if (instrumentId) query.set('instrumentId', instrumentId);

  return useQuery({
    queryKey: [...portfolioKeys.transactions(id), limit, instrumentId ?? 'all'],
    queryFn: ({ signal }) =>
      api.get<{ items: PortfolioTransaction[]; total: number }>(
        `/portfolios/${id}/transactions?${query.toString()}`,
        signal,
      ),
    enabled: options.enabled ?? true,
  });
}

/** Dettaglio di un titolo dentro un portafoglio: posizione, costo, andamento. */
export function usePortfolioInstrument(id: string, instrumentId: string) {
  return useQuery({
    queryKey: portfolioKeys.instrument(id, instrumentId),
    queryFn: ({ signal }) =>
      api.get<PortfolioInstrumentDetail>(`/portfolios/${id}/instruments/${instrumentId}`, signal),
  });
}

/** Registrazione manuale di un movimento (SPEC-008 §5), inclusa l'azione intrapresa da un'analisi AI. */
export function useCreateTransaction(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTransactionInput) =>
      api.post<PortfolioTransaction>(`/portfolios/${portfolioId}/transactions`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.transactions(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.holdings(portfolioId) });
    },
  });
}

/**
 * Valore di carico di un trasferimento in entrata (F-174). `null` torna al
 * valore automatico. Cambia costo medio e P&L: si ricaricano anche posizioni e
 * dettaglio del titolo.
 */
export function useUpdateCostBasis(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, costBasis }: { transactionId: string; costBasis: string | null }) =>
      api.patch<PortfolioTransaction>(`/portfolios/${portfolioId}/transactions/${transactionId}/cost-basis`, {
        costBasis,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.transactions(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.holdings(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.detail(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: ['portfolios', 'instrument', portfolioId] });
    },
  });
}

/** Solo le transazioni manuali non ancora riconciliate da un import si possono eliminare. */
export function useDeleteTransaction(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transactionId: string) =>
      api.delete<void>(`/portfolios/${portfolioId}/transactions/${transactionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.transactions(portfolioId) });
      void queryClient.invalidateQueries({ queryKey: portfolioKeys.holdings(portfolioId) });
    },
  });
}
